import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback } from "react";

const BASE_KEY = "visitedNavItems";

// The guide and every help badge call this hook with the same roundId, so they
// all share one localStorage entry. Each `useLocalStorage` instance keeps its
// own snapshot, and its functional setter merges against that snapshot — so a
// badge writing from a stale (e.g. empty) snapshot can clobber keys another
// instance just wrote. Reading the freshest value straight from storage at
// write time avoids that: JS is single-threaded, so a synchronous read + merge
// + write can't be interleaved by another instance.
export function readVisited(storageKey: string): string[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === "string")
      : [];
  } catch {
    return [];
  }
}

export function useVisitedNavItems(roundId: string | null) {
  const storageKey = roundId
    ? `${BASE_KEY}:${roundId}`
    : `${BASE_KEY}:__none__`;
  const [visited, setVisited] = useLocalStorage<string[]>(storageKey, []);

  const markVisited = useCallback(
    (key: string) => {
      if (!roundId) return;
      // Merge against the latest persisted value, not this instance's snapshot,
      // so concurrent badges don't overwrite each other's keys. Always sync
      // React state to storage — including when the key is already stored (e.g.
      // written by another tab/instance): returning early there would leave a
      // stale snapshot and `isVisited` would keep the badge in its bright "new"
      // style. Re-writing an unchanged set is a no-op render-wise.
      const current = readVisited(storageKey);
      const next = current.includes(key) ? current : [...current, key];
      setVisited(next);
    },
    [roundId, storageKey, setVisited],
  );

  const isVisited = useCallback(
    (key: string) => (roundId ? visited.includes(key) : true),
    [roundId, visited],
  );

  return { visited, markVisited, isVisited };
}
